import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of TableViewSlice — plan §5.2. 39 of web's 63 and mobile's 43 reducers are
 * identical and live here; the state below is all 23 fields mobile and web agree on.
 *
 * Authored per platform instead:
 * - `getAllResourceGroup`, `getAllResources`, `getUsers` — identical BODIES, different
 *   ARITY (web `(state, action)` vs mobile `state =>` for the first two, the reverse for
 *   `getUsers`). That is type-level, not runtime (ledger hazard 14): RTK's creator always
 *   accepts an argument, but a 1-param reducer generates a creator whose TYPE rejects one.
 *
 *   ⚠️ **Checked 2026-08-17: the `action` param CANNOT be dropped to unify these.** In each
 *   case the payload is read by that platform's saga AND passed from a TypeScript call site,
 *   so removing it is a build break — and for `getUsers` it strands a UI callback:
 *     web  `getAllResources`     -> saga reads `action.payload?.skipGlobalLoader`;
 *                                   `getAllResources({})` in saga/login/fetchCatalog.ts
 *     web  `getAllResourceGroup` -> saga reads `action.payload` as skipGlobalLoader;
 *                                   `getAllResourceGroup(hideLoader)` in the same file
 *     mob  `getUsers`            -> saga calls `payload?.callback(response)`;
 *                                   `getUsers({callback: onUserFetchSuccess})` in Login
 *   The one-param sides are equally faithful (mobile's `fetchAllResourceGroups()` declares no
 *   parameter; every web `getUsers()` call site passes nothing). The arity encodes a real
 *   per-platform feature difference. Unifying the OTHER way — adding `PayloadAction<any>` to
 *   the one-param side — would typecheck, but advertises an affordance the other platform's
 *   saga ignores. Leave all three as they are.
 * - `getUsersSuccess` — a real divergence. Web reads `{allUsers, branchId}` off the payload
 *   and builds `branchUsersMap` via `createBranchPermittedUsersMap`; mobile takes the payload
 *   as the user list and builds `saleUsersMap` via `handleSaleUserMap`. Different payload
 *   SHAPE, different helper, different target field.
 * - web's extra reducers (the `*CallComplete` no-ops and the label-print pair; the reservations
 *   cluster was deleted with v1's path, RV6-5) and mobile's 3 (`setSaleUsers`, `saveAllTableData`,
 *   `saveSelectedTableInfo`).
 * - web's `userLogout`/`supportLogout` `extraReducers`, which are EMPTY on purpose — see
 *   index.desktop.ts.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling. Reducers
 * the apps wrote with no `action` param at all keep that shape.
 */
export interface SharedTableViewState {
  allTables: {
    [key: string]: any;
  };
  resourceGroups: {
    [key: string]: any;
  };
  resources: {
    [key: string]: any;
  };
  tableOrdersData: any;
  transferKOTResponse: any;
  transferItemResponse: any;
  transferTableResponse: any;
  cancelKOTResponse: any;
  cancelOrderResponse: any;
  deleteItemResponse: any;
  splitItemsResponse: any;
  splitOrdersResponse: any;
  closeOrderResponse: any;
  sendDigitalBillViaSMSResponse: any;
  sendDigitalBillViaEmailResponse: any;
  sendDigitalBillViaWAResponse: any;
  printResponse: any;
  fetchPrinterResponse: any;
  users: any;
  isTablesDataFetching: boolean;
  currentTableInfo: any;
  printKOTResponse: any;
  isPrintKOTInProgress: any;
  /**
   * Was web-only until 2026-09-12, alongside `printLabelSlice` below. It moved here rather than
   * being copied into `index.native.ts` for the same reason the printKOT pair lives here: the
   * field and its two reducers were byte-identical, so a second copy could only drift.
   */
  isPrintLabelInProgress: any;
}

export const sharedInitialState: SharedTableViewState = {
  allTables: [],
  resourceGroups: [],
  resources: [],
  tableOrdersData: [],
  transferKOTResponse: {},
  transferItemResponse: {},
  transferTableResponse: {},
  cancelKOTResponse: {},
  cancelOrderResponse: {},
  deleteItemResponse: {},
  splitItemsResponse: {},
  splitOrdersResponse: {},
  closeOrderResponse: {},
  sendDigitalBillViaSMSResponse: {},
  sendDigitalBillViaEmailResponse: {},
  sendDigitalBillViaWAResponse: {},
  printResponse: {},
  fetchPrinterResponse: {},
  users: [],
  isTablesDataFetching: false,
  currentTableInfo: {},
  printKOTResponse: {},
  isPrintKOTInProgress: false,
  isPrintLabelInProgress: false,
};

type S = typeof sharedInitialState;

export const sharedReducers = {
  getAllTables: (state: S) => {
    state.isTablesDataFetching = true;
  },
  getAllTablesSuccess: (state: S, action: PayloadAction<any>) => {
    state.allTables = action.payload;
    state.isTablesDataFetching = false;
  },
  getAllResourceGroupSuccess: (state: S, action: PayloadAction<any>) => {
    state.resourceGroups = action.payload;
    state.isTablesDataFetching = false;
  },
  getAllResourcesSuccess: (state: S, action: PayloadAction<any>) => {
    state.resources = action.payload;
    state.isTablesDataFetching = false;
  },
  getTableOrdersData: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  // ⚠️ Sets `isTablesDataFetching = TRUE` on success, on both platforms. Almost certainly a
  // typo for `false` upstream, but it is the current behaviour on both sides and the uplift
  // changes none — do not "fix" it here.
  getTableOrdersDataSuccess: (state: S, action: PayloadAction<any>) => {
    state.tableOrdersData = action.payload;
    state.isTablesDataFetching = true;
  },
  transferKOT: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  transferKOTSuccess: (state: S, action: PayloadAction<any>) => {
    state.transferKOTResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  transferItem: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  transferItemSuccess: (state: S, action: PayloadAction<any>) => {
    state.transferItemResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  transferTable: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  transferTableSuccess: (state: S, action: PayloadAction<any>) => {
    state.transferTableResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  cancelKOT: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  cancelKOTSuccess: (state: S, action: PayloadAction<any>) => {
    state.cancelKOTResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  cancelOrder: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  cancelOrderSuccess: (state: S, action: PayloadAction<any>) => {
    state.cancelOrderResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  deleteItem: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  deleteItemSuccess: (state: S, action: PayloadAction<any>) => {
    state.deleteItemResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  splitItems: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  splitItemsSuccess: (state: S, action: PayloadAction<any>) => {
    state.splitItemsResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  splitOrders: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  splitOrdersSuccess: (state: S, action: PayloadAction<any>) => {
    state.splitOrdersResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  closeOrder: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  closeOrderSuccess: (state: S, action: PayloadAction<any>) => {
    state.closeOrderResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  // Empty on both sides — a saga-only action (the sale is recalculated on the backend).
  calculateSaleFromBE: (state: S, action: PayloadAction<any>) => {},
  sendDigiatlBillViaSMS: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  sendDigitalBillViaSMSSuccess: (state: S, action: PayloadAction<any>) => {
    state.sendDigitalBillViaSMSResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  sendDigiatlBillViaEmail: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  sendDigitalBillViaEmailSuccess: (state: S, action: PayloadAction<any>) => {
    state.sendDigitalBillViaEmailResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  sendDigiatlBillViaWA: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  sendDigitalBillViaWASuccess: (state: S, action: PayloadAction<any>) => {
    state.sendDigitalBillViaWAResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  print: (state: S, action: PayloadAction<any>) => {
    state.isTablesDataFetching = true;
  },
  printSuccess: (state: S, action: PayloadAction<any>) => {
    state.printResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  fetchPrinter: (state: S) => {
    state.isTablesDataFetching = true;
  },
  fetchPrinterSuccess: (state: S, action: PayloadAction<any>) => {
    state.fetchPrinterResponse = action.payload;
    state.isTablesDataFetching = false;
  },
  updateCurrentTableInfo: (state: S, action: PayloadAction<any>) => {
    state.currentTableInfo = action.payload;
  },
  printKOTSlice: (state: S, action: PayloadAction<any>) => {
    console.log("working");
    state.isPrintKOTInProgress = true;
  },
  printKOTSuccess: (state: S, action: PayloadAction<any>) => {
    state.printKOTResponse = action.payload;
    state.isPrintKOTInProgress = false;
  },
  /**
   * Acknowledge printed item labels back to the server. `printLabelSlice` is the REQUEST — the
   * printer saga's `updateLabelPrintStatus` watches it and POSTs `{ labelInfos }` to
   * `sale/{id}/itemlabel/print/status`.
   *
   * ⚠️ Both were WEB-ONLY until 2026-09-12 (declared in `index.desktop.ts`), which is why the
   * other platform never wrote label print status back. Moved here — not copied — when mobile
   * gained the worker; there is exactly one definition of each.
   *
   * ⚠️ `printLabelSuccess` has no dispatcher on either platform: the saga reports through the
   * action's own `callback`, not through the store. Kept because web declared it and because a
   * "request" reducer that can never be cleared is a worse shape than an unused pair.
   */
  printLabelSlice: (state: S, action: PayloadAction<any>) => {
    state.isPrintLabelInProgress = true;
  },
  printLabelSuccess: (state: S, action: PayloadAction<any>) => {
    state.isPrintLabelInProgress = false;
  },
  // ⚠️ Note what it does NOT clear: `sendDigitalBillViaWAResponse` is missing from this list
  // on BOTH platforms while its SMS and Email siblings are cleared. Preserved verbatim.
  //
  // ⚠️ apps/mobile declares this reducer TWICE (ledger hazard 2, ~lines 217 and 232 of the
  // app file); the second silently wins. Both bodies are identical, and identical to web's,
  // so the single definition here is the effective one — not a behaviour change.
  emptyTableViewResponses: (state: S) => {
    state.transferKOTResponse = {};
    state.transferItemResponse = {};
    state.transferTableResponse = {};
    state.cancelKOTResponse = {};
    state.cancelOrderResponse = {};
    state.deleteItemResponse = {};
    state.splitItemsResponse = {};
    state.splitOrdersResponse = {};
    state.closeOrderResponse = {};
    state.sendDigitalBillViaSMSResponse = {};
    state.sendDigitalBillViaEmailResponse = {};
    state.printResponse = {};
    state.fetchPrinterResponse = {};
  },
};
